/**
 * <cart-drawer> — slide-over cart that re-renders via Section Rendering API.
 *
 * Listens:
 *   - "cart:updated" on ThemeBus → swap inner section from supplied sections OR refetch
 *   - "cart:open" on ThemeBus → show drawer
 *   - clicks on [data-cart-drawer-open], [data-cart-drawer-close], [data-cart-drawer-overlay]
 *   - line-item +/-/remove inside the drawer
 */

class CartDrawer extends globalThis.ThemeElement {
  connectedCallback() {
    this.sectionId = this.dataset.sectionId;
    this.threshold = parseInt(this.dataset.threshold || '0', 10);
    this._releaseFocus = null;
    this._setupTriggers();
    this.on(globalThis.ThemeBus, 'cart:updated', (e) => this.handleUpdate(e.detail));
    this.on(globalThis.ThemeBus, 'cart:open', () => this.open());
    this.on(this, 'click', this.handleLineEvent.bind(this));
    this.on(this, 'change', this.handleLineEvent.bind(this));
    this._loadRecommendationsIfNeeded();
  }

  _setupTriggers() {
    document.querySelectorAll('[data-cart-drawer-open]').forEach((btn) => {
      this.on(btn, 'click', (e) => { e.preventDefault(); this.open(); });
    });
    document.querySelectorAll('[data-cart-drawer-close]').forEach((btn) => {
      this.on(btn, 'click', (e) => { e.preventDefault(); this.close(); });
    });
    const overlay = document.querySelector('[data-cart-drawer-overlay]');
    if (overlay) this.on(overlay, 'click', () => this.close());
    this.on(window, 'keydown', (e) => { if (e.key === 'Escape' && this.isOpen) this.close(); });
  }

  get isOpen() { return this.hasAttribute('open'); }

  open() {
    if (this.isOpen) return;
    this.setAttribute('open', '');
    this.removeAttribute('hidden');
    this.setAttribute('aria-hidden', 'false');
    globalThis.JunneTheme.lockBodyScroll();
    this._releaseFocus = globalThis.JunneTheme.trapFocus(this);
    this.emit('drawer:open', { name: 'cart' });
    this._loadRecommendationsIfNeeded();
  }

  close() {
    if (!this.isOpen) return;
    this.removeAttribute('open');
    this.setAttribute('aria-hidden', 'true');
    globalThis.JunneTheme.unlockBodyScroll();
    this._releaseFocus?.();
    this._releaseFocus = null;
    /* keep DOM mounted; just hide visually */
    setTimeout(() => { if (!this.isOpen) this.setAttribute('hidden', ''); }, 300);
    this.emit('drawer:close', { name: 'cart' });
  }

  async handleUpdate(detail) {
    /* If the producer (e.g. product-form) supplied rendered sections, use them. */
    const html = detail?.sections?.['cart-drawer'];
    if (html) {
      this.swapFromHtml(html);
    } else {
      /* Otherwise refetch */
      try {
        const text = await globalThis.JunneTheme.fetchSection(this.sectionId);
        this.swapFromHtml(text);
      } catch (err) { console.error('cart-drawer refresh', err); }
    }
    /* After swap, `this` is disconnected. Operate on the new in-DOM element. */
    const liveDrawer = document.querySelector('cart-drawer') || this;
    /* Update bag count badge in header */
    const cartRes = await fetch(`${(globalThis.Shopify?.routes?.root || '/')}cart.js`);
    if (cartRes.ok) {
      const cart = await cartRes.json();
      liveDrawer._updateCount?.(cart.item_count) || this._updateCount(cart.item_count);
    }
    liveDrawer.open?.() || this.open();
  }

  swapFromHtml(html) {
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const newSection = parsed.querySelector(`#shopify-section-${this.sectionId} cart-drawer`)
      || parsed.querySelector('cart-drawer');
    if (!newSection) return;
    /* preserve open + hidden + aria state so the new element matches the visual state */
    if (this.isOpen) {
      newSection.setAttribute('open', '');
      newSection.setAttribute('aria-hidden', 'false');
      newSection.removeAttribute('hidden');
    }
    this.replaceWith(newSection);
  }

  _updateCount(count) {
    document.querySelectorAll('[data-cart-count]').forEach((el) => {
      el.textContent = count;
      el.style.display = count === 0 ? 'none' : '';
    });
    document.querySelectorAll('[data-cart-count-label]').forEach((el) => { el.textContent = `(${count})`; });
  }

  async _loadRecommendationsIfNeeded() {
    /* If the cart-drawer section already rendered recommendation cards inline
       (from the bestsellers fallback in the Liquid template), do nothing.
       Otherwise try Shopify's real recommendations endpoint and inject results. */
    const host = this.querySelector('[data-cart-recommendations]');
    if (!host) return;
    const grid = host.querySelector('[data-cart-rec-grid]');
    if (!grid) return;
    if (grid.children.length > 0) return; /* server already rendered fallback */
    const productId = host.dataset.productId;
    if (!productId) return;
    try {
      const url = `${(globalThis.Shopify?.routes?.root || '/')}recommendations/products?intent=related&product_id=${productId}&limit=4&section_id=${this.sectionId}`;
      const res = await fetch(url);
      if (!res.ok) return;
      const text = await res.text();
      const parsed = new DOMParser().parseFromString(text, 'text/html');
      const cards = parsed.querySelectorAll('[data-product-card]');
      cards.forEach((c) => grid.appendChild(c));
    } catch (err) { /* silent */ }
  }

  /* Line item interactions */
  async handleLineEvent(e) {
    const line = e.target.closest('[data-cart-line]');
    if (!line) return;
    const key = line.dataset.key;
    const decBtn = e.target.closest('[data-line-decrement]');
    const incBtn = e.target.closest('[data-line-increment]');
    const removeBtn = e.target.closest('[data-line-remove]');
    const qtyInput = e.target.closest('[data-line-quantity]');
    const qInput = line.querySelector('[data-line-quantity]');
    let newQty = parseInt(qInput?.value || '1', 10);

    if (decBtn) { e.preventDefault(); newQty = Math.max(0, newQty - 1); }
    else if (incBtn) { e.preventDefault(); newQty = newQty + 1; }
    else if (removeBtn) { e.preventDefault(); newQty = 0; }
    else if (qtyInput && e.type === 'change') { newQty = parseInt(qtyInput.value, 10) || 0; }
    else return;

    line.classList.add('opacity-50', 'pointer-events-none');
    try {
      const res = await fetch(`${(globalThis.Shopify?.routes?.root || '/')}cart/change.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ id: key, quantity: newQty, sections: this.sectionId, sections_url: window.location.pathname })
      });
      if (!res.ok) throw new Error(res.status);
      const data = await res.json();
      globalThis.ThemeBus.dispatchEvent(new CustomEvent('cart:updated', { detail: { sections: data.sections } }));
    } catch (err) {
      console.error('cart line update', err);
      line.classList.remove('opacity-50', 'pointer-events-none');
    }
  }
}

if (!customElements.get('cart-drawer')) customElements.define('cart-drawer', CartDrawer);
